import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  SelectOption,
  UserPermission
} from '@globaleris/ui.library'
import { Button, CardContent, CardHeader, makeStyles, useTheme } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { LanguageModel } from '../../../../../api/Argentia/model/contracts'
import { CardWithTransition } from '../../../../../components/shared/cardWithTransition/CardWithTransition'
import { LoadingCard } from '../../../../../components/shared/loadingCard/loadingCard'
import { generateUniqueKeyWithNoPrefix } from '../../../../../helpers'
import { associateActionIcon } from '../../../../../helpers/dataTableActionIconHelper'
import { getUserPermissions } from '../../../../../helpers/permissionsHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../../hooks'
import { ActionIcons } from '../../../../../model'
import { Pages } from '../../../../../model/RBAC/constants'
import { getCustomerDomain } from '../../../../../redux'
import { CardEmptyContent } from '../shared/cardEmptyContent/cardEmptyContent'
import { getUnusedTranslations } from './translationsCard.helpers'
import {
  translationTableModelMapperToTranslationsModel,
  translationsToTranslationTableModelMapper
} from './translationsCard.mapper'
import { ProductTranslationModel, ProductTranslationTableModel } from './translationsCard.model'
import { styles } from './translationsCard.styles'

export interface TranslationsCardProps {
  translations: ProductTranslationModel[]
  languages: LanguageModel[]
  onAddTranslationsRowValues: (formData: ProductTranslationModel) => void
  onEditTranslationsRowValues: (rowData: ProductTranslationModel) => void
  onDeleteTranslationsRowValues: (rowData: ProductTranslationModel) => void
  isFetching: boolean
}

export interface EditFormDependenciesType {
  formState: any
  translationsOptions: SelectOption[]
}

export const TranslationsCard: React.FC<TranslationsCardProps> = ({
  translations,
  languages,
  onAddTranslationsRowValues,
  onEditTranslationsRowValues,
  onDeleteTranslationsRowValues,
  isFetching
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'productsEdit', 'enums', 'dataTable'])
  const history = useHistory()
  const { openForm, closeForm, updateFormDefinition, formProps, CustomFormDialog } = useFormDialog()
  const [editFormDependencies, setEditFormDependencies] = useState<EditFormDependenciesType>({
    formState: {},
    translationsOptions: []
  })

  // Import Styles
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  //#region Handlers Starts
  const onAddTranslationHandler = async () => {
    const unusedTranslations: SelectOption[] = getUnusedTranslations(translations, languages)

    openForm({
      definition: translationFormDefinition,
      title: t('productsEdit:forms.edit.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: onAddTranslationCloseHandler
    })

    setEditFormDependencies({
      formState: {} as ProductTranslationTableModel,
      translationsOptions: unusedTranslations
    })

    return true
  }

  const onAddTranslationCloseHandler = (formData: ProductTranslationTableModel) => {
    // Generate tempId to added data to render ActionIcon later
    if (formData) {
      const formDataWithTempId = {
        ...formData,
        tempId: generateUniqueKeyWithNoPrefix()
      }

      // Map back Table to Model and modify state
      onAddTranslationsRowValues(translationTableModelMapperToTranslationsModel(formDataWithTempId))
    }

    // Clean dependency state
    setEditFormDependencies({} as EditFormDependenciesType)

    closeForm()
  }

  const onEditTranslationHandler = row => {
    const unusedTranslations: SelectOption[] = getUnusedTranslations(translations, languages, row)

    openForm({
      definition: translationFormDefinition,
      title: t('productsEdit:forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      // inputData: row,
      onClose: onEditTranslationCloseHandler
    })

    setEditFormDependencies({
      ...editFormDependencies,
      formState: row,
      translationsOptions: unusedTranslations
    })
  }

  const onEditTranslationCloseHandler = (row: ProductTranslationTableModel) => {
    if (row) {
      // Generate tempId to added data to render ActionIcon later
      let rowWithTempId: ProductTranslationTableModel
      if (row.tempId) {
        rowWithTempId = row
      } else {
        rowWithTempId = {
          ...row,
          tempId: generateUniqueKeyWithNoPrefix()
        }
      }
      // Map back Table to Model and modify state
      onEditTranslationsRowValues(translationTableModelMapperToTranslationsModel(rowWithTempId))
    }
    // Clean dependency state
    setEditFormDependencies({} as EditFormDependenciesType)

    closeForm()
  }

  const onDeleteTranslationHandler = (row: ProductTranslationTableModel) => {
    // Map back Table to Model and modify state
    onDeleteTranslationsRowValues(translationTableModelMapperToTranslationsModel(row))

    // Clean dependency state
    setEditFormDependencies({} as EditFormDependenciesType)
  }

  //#endregion Handlers Ends

  //#region Form Definition Starts

  const translationFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_ADD_TRANSLATIONS',
        sections: [
          {
            inputs: [
              {
                name: 'description',
                label: t('productsEdit:components.translationsCard.editDialog.description'),
                type: DataType.TEXT
              },
              {
                name: 'language',
                label: t('productsEdit:components.translationsCard.editDialog.language'),
                type: DataType.SELECT,
                // defaultValue: editFormDependencies.formState.language,
                selectOptions:
                  editFormDependencies?.translationsOptions?.length > 0
                    ? editFormDependencies?.translationsOptions
                    : [{ value: 0, label: '...' }]
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editFormDependencies])

  //#endregion Form Definition Ends

  //#region DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'description',
      label: t('productsEdit:components.translationsCard.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'language',
      label: t('productsEdit:components.translationsCard.dataTable.columns.language'),
      selectOptions: languages.map(l => ({ value: l.id, label: l.description })),
      type: DataType.SELECT
    },
    {
      name: 'default',
      label: t('productsEdit:components.translationsCard.dataTable.columns.default'),
      type: DataType.BOOLEAN
    }
  ]

  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: true,
    customInsertEditModal: true,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,

    analytics: {
      customer: getCustomerDomain(),
      view: t('productsEdit:components.translationsCard.dataTable.analytics')
    },
    onRowInsert: onAddTranslationHandler,
    rowCustomActions: [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('productsEdit:components.translationsCard.dataTable.actions.edit'),
        onClick: onEditTranslationHandler,
        userPermission: UserPermission.EDIT
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t('productsEdit:components.translationsCard.dataTable.actions.delete'),
        onClick: onDeleteTranslationHandler,
        userPermission: UserPermission.EDIT,
        visibleIf: 'notDefault'
      }
    ]
  }
  //#endregion DataTable Config Ends

  //#region Functions Starts

  const loadNotSaveIconsToTranslationsList = (
    dataTableList: ProductTranslationModel[]
  ): ProductTranslationModel[] => {
    if (dataTableList && dataTableList.length > 0) {
      return dataTableList.map(t => {
        if (t.tempId) {
          const actionIcon = associateActionIcon(t, ActionIcons.NEW_ITEM, 'No Guardado')
          return {
            ...t,
            actions: actionIcon
          }
        }
        return t
      })
    }
    return dataTableList
  }

  const translationsList = loadNotSaveIconsToTranslationsList(translations)

  //#endregion Functions Ends

  //#region Hooks Starts
  useEffect(() => {
    updateFormDefinition(translationFormDefinition, editFormDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationFormDefinition])

  //#endregion Hooks Ends

  if (isFetching) {
    return <LoadingCard />
  }

  if (translations.length === 0) {
    return (
      <>
        <CardWithTransition show={true} raised>
          <CardHeader
            title={t('productsEdit:components.translationsCard.title')}
            titleTypographyProps={{ variant: 'h2', color: 'secondary' }}
          />
          <CardEmptyContent title={t('productsEdit:components.translationsCard.emptyCard.title')}>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={onAddTranslationHandler}
            >
              {t('productsEdit:components.translationsCard.emptyCard.buttonLabel')}
            </Button>
          </CardEmptyContent>
        </CardWithTransition>
        <CustomFormDialog {...formProps} />
      </>
    )
  }

  return (
    <>
      <CardWithTransition show={true} raised>
        <CardHeader
          title={t('productsEdit:components.translationsCard.title')}
          titleTypographyProps={{ variant: 'h2', color: 'secondary' }}
        />
        <CardContent className={classes.cardContent}>
          <DataTable
            className={classes.dataTableStyle}
            data={translationsToTranslationTableModelMapper(translationsList)}
            columns={columns}
            options={tableOptions}
            userPermissions={getUserPermissions(Pages.Client)}
          />
        </CardContent>
      </CardWithTransition>
      <CustomFormDialog {...formProps} />
    </>
  )
}
